import { useCallback, useSyncExternalStore } from 'react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { msalInstance } from '@/lib/msal-config';

// One shared app registration serves as both the SPA client and the API
// audience (docs/chat-backend-infra-setup.md §2), so the scope is built from
// the same client ID MSAL signs in with.
const JENNY_SCOPES = [`api://${import.meta.env.VITE_AZURE_CLIENT_ID}/access_as_user`];

interface UseAppTokenReturn {
  getToken: () => Promise<string>;
  isReady: boolean;
  error: Error | null;
}

let initState: { ready: boolean; error: Error | null; loginHint?: string } = {
  ready: false,
  error: null,
};
let listeners: (() => void)[] = [];

function subscribe(cb: () => void) {
  listeners.push(cb);
  return () => {
    listeners = listeners.filter((l) => l !== cb);
  };
}

function getSnapshot() {
  return initState;
}

function notify() {
  for (const cb of listeners) {
    cb();
  }
}

async function fetchLoginHint(): Promise<string | undefined> {
  try {
    const res = await fetch('/.auth/me');
    if (!res.ok) return undefined;
    const data = (await res.json()) as {
      clientPrincipal: { userDetails?: string; claims?: { typ: string; val: string }[] } | null;
    };
    const claims = data.clientPrincipal?.claims;
    return (
      claims?.find((c) => c.typ === 'preferred_username')?.val ?? data.clientPrincipal?.userDetails
    );
  } catch {
    return undefined;
  }
}

let initPromise: Promise<void> = Promise.resolve();

if (import.meta.env.DEV && import.meta.env.VITE_CHATBOT_DEV_TOKEN) {
  initState = { ready: true, error: null };
} else {
  initPromise = Promise.all([msalInstance.initialize(), fetchLoginHint()]).then(
    ([, loginHint]) => {
      initState = { ready: true, error: null, loginHint };
      notify();
    },
    (err: unknown) => {
      initState = { ready: false, error: err instanceof Error ? err : new Error(String(err)) };
      notify();
    },
  );
}

/**
 * Access token for the Jenny app registration. Shared by the chatbot (bearer
 * auth) and the role check (the OneIDM role surfaces as a claim in it).
 * `interactive: false` never opens a popup — use it outside a user gesture.
 */
export async function acquireAppToken({
  interactive = true,
  forceRefresh = false,
} = {}): Promise<string> {
  if (import.meta.env.DEV && import.meta.env.VITE_CHATBOT_DEV_TOKEN) {
    return import.meta.env.VITE_CHATBOT_DEV_TOKEN;
  }

  await initPromise;
  const popup = async () =>
    (await msalInstance.acquireTokenPopup({ scopes: JENNY_SCOPES })).accessToken;

  const account = msalInstance.getAllAccounts()[0];

  if (account) {
    try {
      const response = await msalInstance.acquireTokenSilent({
        scopes: JENNY_SCOPES,
        account,
        forceRefresh,
      });
      return response.accessToken;
    } catch (err) {
      if (interactive && err instanceof InteractionRequiredAuthError) return popup();
      throw err;
    }
  }

  try {
    const response = await msalInstance.ssoSilent({
      scopes: JENNY_SCOPES,
      loginHint: initState.loginHint,
    });
    return response.accessToken;
  } catch (err) {
    if (interactive) return popup();
    throw err;
  }
}

export function useAppToken(): UseAppTokenReturn {
  const snapshot = useSyncExternalStore(subscribe, getSnapshot);
  const getToken = useCallback(() => acquireAppToken(), []);
  return { getToken, isReady: snapshot.ready, error: snapshot.error };
}
